import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  Download,
  LogIn,
  LogOut,
  Mail,
  RadioTower,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Unplug,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { absTime, initials, relTime } from "@/lib/format";
import { errMessage, listAudit } from "@/lib/api";

const PAGE = 50;

const ACTION_META = {
  login_success: { icon: LogIn, tone: "border-success-border bg-success-surface text-success-text" },
  login_denied: { icon: ShieldAlert, tone: "border-danger-border bg-danger-surface text-danger-text" },
  logout: { icon: LogOut, tone: "border-border bg-secondary text-muted-foreground" },
  gmail_connect: { icon: Mail, tone: "border-success-border bg-success-surface text-success-text" },
  gmail_disconnect: { icon: Unplug, tone: "border-danger-border bg-danger-surface text-danger-text" },
  gmail_watch_start: { icon: RadioTower, tone: "border-info-border bg-info-surface text-info-text" },
  gmail_watch_stop: { icon: RadioTower, tone: "border-warning-border bg-warning-surface text-warning-text" },
  settings_update: { icon: SettingsIcon, tone: "border-info-border bg-info-surface text-info-text" },
  settings_csv_reset: { icon: SettingsIcon, tone: "border-warning-border bg-warning-surface text-warning-text" },
  export_run: { icon: Download, tone: "border-info-border bg-info-surface text-info-text" },
  user_role_change: { icon: UserCog, tone: "border-info-border bg-info-surface text-info-text" },
  user_access_revoked: { icon: ShieldAlert, tone: "border-danger-border bg-danger-surface text-danger-text" },
};

const ActionBadge = ({ action, label }) => {
  const meta = ACTION_META[action] || {
    icon: Activity,
    tone: "border-border bg-secondary text-foreground",
  };
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10.5px] font-semibold",
        meta.tone
      )}
      data-testid={`audit-action-${action}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {label || action}
    </span>
  );
};

const changeChips = (event) => {
  const changes = event?.meta?.changes;
  if (changes && typeof changes === "object") {
    return Object.entries(changes).map(([key, val]) => ({
      key,
      text: `${key}: ${val?.from ?? "—"} → ${val?.to ?? "—"}`,
    }));
  }
  const meta = event?.meta || {};
  return Object.entries(meta)
    .filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length) && v !== false)
    .slice(0, 4)
    .map(([key, v]) => ({ key, text: `${key}: ${Array.isArray(v) ? v.join(", ") : String(v)}` }));
};

export default function ActivityPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [actors, setActors] = useState([]);
  const [action, setAction] = useState("all");
  const [actor, setActor] = useState("all");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (opts = {}) => {
      const nextSkip = opts.skip ?? 0;
      setLoading(true);
      try {
        const data = await listAudit({
          action: (opts.action ?? action) === "all" ? undefined : (opts.action ?? action),
          actor: (opts.actor ?? actor) === "all" ? undefined : (opts.actor ?? actor),
          q: (opts.q ?? q) || undefined,
          limit: PAGE,
          skip: nextSkip,
        });
        setItems((prev) => (nextSkip ? [...prev, ...(data.items || [])] : data.items || []));
        setTotal(data.total || 0);
        setActions(data.actions || []);
        setActors(data.actors || []);
        setSkip(nextSkip);
      } catch (e) {
        toast.error("Could not load the activity log", { description: errMessage(e) });
      } finally {
        setLoading(false);
      }
    },
    [action, actor, q]
  );

  useEffect(() => {
    load({ skip: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, actor]);

  useEffect(() => {
    const t = setTimeout(() => load({ skip: 0 }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const usedActions = useMemo(
    () => [{ key: "all", label: "All activity" }, ...actions],
    [actions]
  );

  return (
    <div className="space-y-4" data-testid="activity-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight sm:text-xl">Activity log</h1>
          <p className="text-xs text-muted-foreground sm:text-sm">
            Who signed in, who changed settings, who connected Gmail and who exported what.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium"
            data-testid="activity-total"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            {total} event{total === 1 ? "" : "s"}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 bg-card"
            onClick={() => load({ skip: 0 })}
            data-testid="activity-refresh-button"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="vde-card p-2.5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="h-9 w-[190px] bg-card text-xs" data-testid="activity-action-filter">
                <SelectValue placeholder="All activity" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {usedActions.map((a) => (
                  <SelectItem key={a.key} value={a.key} className="text-xs">
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actor} onValueChange={setActor}>
              <SelectTrigger className="h-9 w-[220px] bg-card text-xs" data-testid="activity-actor-filter">
                <SelectValue placeholder="Everyone" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="all" className="text-xs">
                  Everyone
                </SelectItem>
                {actors.map((a) => (
                  <SelectItem key={a} value={a} className="text-xs">
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative w-full lg:w-80">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search summary, person, id…"
              className="h-9 bg-card pl-8 text-xs"
              data-testid="activity-search-input"
            />
          </div>
        </div>
      </div>

      {loading && !items.length ? (
        <div className="vde-card space-y-2 p-4" data-testid="activity-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : null}

      {!loading && !items.length ? (
        <div className="vde-card p-8 text-center" data-testid="activity-empty">
          <p className="text-sm font-semibold">Nothing logged yet</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
            Sign-ins, settings changes, Gmail connections and exports will show up here as soon as
            they happen.
          </p>
        </div>
      ) : null}

      {items.length ? (
        <>
          {/* desktop table */}
          <div className="vde-card vde-scroll hidden overflow-x-auto md:block">
            <table className="w-full min-w-[880px] border-collapse text-left" data-testid="activity-table">
              <thead>
                <tr className="border-b border-border bg-secondary">
                  {["When", "Who", "Action", "Details"].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((ev) => (
                  <tr
                    key={ev.id}
                    className="border-b border-border align-top hover:bg-secondary/50"
                    data-testid="activity-row"
                  >
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <p className="text-[12px] font-medium">{relTime(ev.created_at)}</p>
                      <p className="vde-code text-[10px] text-muted-foreground">
                        {absTime(ev.created_at)}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-primary">
                          {initials(ev.actor_name || ev.actor_email || "?")}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[12px] font-medium" data-testid="activity-actor">
                            {ev.actor_email || "system"}
                          </p>
                          {ev.actor_role ? (
                            <p className="text-[10px] text-muted-foreground">{ev.actor_role}</p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <ActionBadge action={ev.action} label={ev.action_label} />
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-[12px] leading-snug" data-testid="activity-summary">
                        {ev.summary || "—"}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {changeChips(ev).map((c) => (
                          <span
                            key={c.key}
                            className="vde-code max-w-[420px] truncate rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"
                            title={c.text}
                          >
                            {c.text}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="space-y-2 md:hidden">
            {items.map((ev) => (
              <div key={ev.id} className="vde-card p-3" data-testid="activity-card">
                <div className="flex items-start justify-between gap-2">
                  <ActionBadge action={ev.action} label={ev.action_label} />
                  <span className="vde-code shrink-0 text-[10px] text-muted-foreground">
                    {relTime(ev.created_at)}
                  </span>
                </div>
                <p className="mt-2 text-[12.5px] leading-snug">{ev.summary || "—"}</p>
                <p className="mt-1 truncate text-[11px] text-muted-foreground">
                  {ev.actor_email || "system"}
                </p>
              </div>
            ))}
          </div>

          {items.length < total ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                className="bg-card"
                onClick={() => load({ skip: skip + PAGE })}
                disabled={loading}
                data-testid="activity-load-more"
              >
                {loading ? "Loading…" : `Load ${Math.min(PAGE, total - items.length)} more`}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
